import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { endpoints, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Copy, Archive, ArchiveRestore, Trash2, FileText, Clock, Users, DollarSign } from "lucide-react";

const ACCESS_LABEL = { free: "Free", paid_online: "Paid", pay_at_door: "Pay at door" };

export default function VenueTemplates() {
    const { venue, role } = useOutletContext();
    const canEdit = role === "manager" || role === "admin";
    const [showArchived, setShowArchived] = useState(false);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);

    const load = () => {
        setLoading(true);
        endpoints.venueTemplates(venue.id, showArchived).then(setData).finally(() => setLoading(false));
    };
    useEffect(load, [venue.id, showArchived]);

    const doDuplicate = async (t) => {
        setBusyId(t.id);
        try { await endpoints.venueDuplicateTemplate(venue.id, t.id); toast.success("Duplicated"); load(); }
        catch (e) { toast.error(formatApiError(e)); } finally { setBusyId(null); }
    };
    const doArchive = async (t) => {
        setBusyId(t.id);
        try {
            await endpoints.venueArchiveTemplate(venue.id, t.id, !t.archived);
            toast.success(t.archived ? "Restored" : "Archived");
            load();
        } catch (e) { toast.error(formatApiError(e)); } finally { setBusyId(null); }
    };
    const doDelete = async (t) => {
        if (!window.confirm(`Delete template "${t.name}"? This cannot be undone.`)) return;
        setBusyId(t.id);
        try { await endpoints.venueDeleteTemplate(venue.id, t.id); toast.success("Deleted"); load(); }
        catch (e) { toast.error(formatApiError(e)); } finally { setBusyId(null); }
    };

    const templates = data?.templates || [];

    return (
        <div className="pm-fade" data-testid="venue-templates-page">
            <header className="border-b border-[#2a2a2a] px-4 md:px-8 py-6 flex items-center justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Templates</div>
                    <h1 className="font-display font-black uppercase tracking-tighter text-3xl md:text-4xl">Event templates</h1>
                    <p className="mt-1 text-xs text-[#a3a3a3] max-w-2xl">Save common event setups so managers can spin up nights faster. Templates never store dates, artists, or event-specific images.</p>
                </div>
                <div className="flex gap-2 items-center">
                    <label className="flex items-center gap-2 text-[10px] font-mono uppercase text-[#a3a3a3] cursor-pointer" data-testid="venue-templates-show-archived-label">
                        <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="accent-[#ff4400]" data-testid="venue-templates-show-archived" />
                        Show archived
                    </label>
                    {canEdit && (
                        <Link to={`/venue/${venue.id}/templates/new`} className="pm-btn pm-btn-primary" data-testid="venue-template-new-btn">
                            <Plus className="w-4 h-4 mr-2" /> New template
                        </Link>
                    )}
                </div>
            </header>

            <div className="px-4 md:px-8 py-6">
                {loading ? (
                    <div className="font-mono text-xs text-[#666]" data-testid="venue-templates-loading">Loading…</div>
                ) : templates.length === 0 ? (
                    <div className="border border-dashed border-[#2a2a2a] p-10 text-center" data-testid="venue-templates-empty">
                        <FileText className="w-8 h-8 text-[#666] mx-auto" />
                        <div className="mt-3 text-sm text-[#a3a3a3]">No templates yet. Save common setups so future events take one tap.</div>
                        {canEdit && (
                            <Link to={`/venue/${venue.id}/templates/new`} className="pm-btn pm-btn-primary mt-4 inline-flex" data-testid="venue-templates-empty-cta">Create a template</Link>
                        )}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {templates.map((t) => (
                            <div key={t.id} className={`border p-4 ${t.archived ? "border-[#2a2a2a] opacity-70" : "border-[#2a2a2a]"}`} data-testid={`venue-template-${t.id}`}>
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="text-[10px] font-mono uppercase text-[#666] flex items-center gap-2">
                                            <span>{t.event_type || "concert"}</span>
                                            {t.archived && <span className="text-[#ff9500]">· Archived</span>}
                                        </div>
                                        <div className="font-display font-bold uppercase text-lg truncate" data-testid={`venue-template-name-${t.id}`}>{t.name}</div>
                                        <div className="mt-2 flex flex-wrap gap-3 text-[10px] font-mono uppercase text-[#a3a3a3]">
                                            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {t.start_time}–{t.end_time}</span>
                                            <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {t.capacity}</span>
                                            <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" /> {ACCESS_LABEL[t.access_mode] || t.access_mode}{!t.is_free && t.ticket_price ? ` · $${t.ticket_price}` : ""}</span>
                                        </div>
                                        <div className="mt-1 text-[10px] font-mono uppercase text-[#666]" data-testid={`venue-template-usage-${t.id}`}>
                                            Used {t.usage_count || 0}x · Updated {new Date(t.updated_at).toLocaleDateString()}
                                        </div>
                                        {t.description && <div className="mt-2 text-xs text-[#a3a3a3] line-clamp-2">{t.description}</div>}
                                    </div>
                                </div>
                                {canEdit && (
                                    <div className="mt-3 pt-3 border-t border-[#2a2a2a] flex flex-wrap gap-2">
                                        <Link to={`/venue/${venue.id}/events/new?template=${t.id}`} className="pm-btn pm-btn-primary text-[10px]" data-testid={`venue-template-use-${t.id}`}>
                                            Use template →
                                        </Link>
                                        <Link to={`/venue/${venue.id}/templates/${t.id}/edit`} className="pm-btn text-[10px]" data-testid={`venue-template-edit-${t.id}`}>Edit</Link>
                                        <button onClick={() => doDuplicate(t)} disabled={busyId === t.id} className="pm-btn text-[10px]" data-testid={`venue-template-duplicate-${t.id}`}>
                                            <Copy className="w-3 h-3 mr-1" /> Duplicate
                                        </button>
                                        <button onClick={() => doArchive(t)} disabled={busyId === t.id} className="pm-btn text-[10px]" data-testid={`venue-template-archive-${t.id}`}>
                                            {t.archived ? (<><ArchiveRestore className="w-3 h-3 mr-1" /> Restore</>) : (<><Archive className="w-3 h-3 mr-1" /> Archive</>)}
                                        </button>
                                        {(t.usage_count || 0) === 0 && (
                                            <button onClick={() => doDelete(t)} disabled={busyId === t.id} className="pm-btn text-[10px] text-[#ff3333] border-[#ff3333]" data-testid={`venue-template-delete-${t.id}`}>
                                                <Trash2 className="w-3 h-3 mr-1" /> Delete
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
